import { db } from '../firebase';
import firebase from 'firebase/app';

const PAGE_SIZE = 20;

export async function getDuas({ page = 1, category, subCategoryId, status, search, pageSize = 20 } = {}) {
  // Fetch all active duas to bypass complex composite index requirements
  const snapshot = await db.collection('duas').where('isDeleted', '==', false).get();
  
  let items = snapshot.docs
    .map(doc => ({ id: doc.id, ...doc.data() }))
    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

  // Apply filters locally
  if (category && category !== 'all') {
    items = items.filter(d => d.category === category);
  }
  if (subCategoryId && subCategoryId !== 'all') {
    items = items.filter(d => d.subCategoryId === subCategoryId);
  }
  if (status && status !== 'all') {
    const isPub = status === 'published';
    items = items.filter(d => !!d.isPublished === isPub);
  }

  // Apply search locally
  if (search) {
    const s = search.toLowerCase();
    items = items.filter(d => 
      (d.title?.en && d.title.en.toLowerCase().includes(s)) || 
      (d.title?.bn && d.title.bn.toLowerCase().includes(s)) ||
      (d.textArabic && d.textArabic.includes(s))
    );
  }

  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const validPage = Math.max(1, Math.min(page, totalPages));
  const startIndex = (validPage - 1) * pageSize;
  const paginatedItems = items.slice(startIndex, startIndex + pageSize);
  
  return { 
    items: paginatedItems, 
    total,
    totalPages,
    page: validPage,
    pageSize,
    hasMore: startIndex + pageSize < total 
  };
}

export async function getPrevDuas({ endBefore, category, subCategoryId, status } = {}) {
  let query = db.collection('duas').where('isDeleted', '==', false);
  
  if (category && category !== 'all') query = query.where('category', '==', category);
  if (subCategoryId && subCategoryId !== 'all') query = query.where('subCategoryId', '==', subCategoryId);
  if (status && status !== 'all') query = query.where('isPublished', '==', status === 'published');
  
  query = query.orderBy('orderIndex').limitToLast(PAGE_SIZE);
  if (endBefore) query = query.endBefore(endBefore);
  
  const snapshot = await query.get();
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

export async function getDuaById(id) {
  const doc = await db.collection('duas').doc(id).get();
  if (!doc.exists) return null;
  return { id: doc.id, ...doc.data() };
}

export async function createDua(data) {
  const maxOrder = await db.collection('duas').orderBy('orderIndex', 'desc').limit(1).get();
  const orderIndex = maxOrder.empty ? 0 : (maxOrder.docs[0].data().orderIndex || 0) + 1;
  return db.collection('duas').add({
    ...data,
    orderIndex,
    isPublished: data.isPublished || false,
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    created_at: firebase.firestore.FieldValue.serverTimestamp(),
    updated_at: "",
    deleted_at: "",
  });
}

export async function updateDua(id, data) {
  return db.collection('duas').doc(id).update({
    ...data,
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    updated_at: firebase.firestore.FieldValue.serverTimestamp(),
  });
}

export async function deleteDua(id) {
  return db.collection('duas').doc(id).update({
    isDeleted: true,
    deletedAt: firebase.firestore.FieldValue.serverTimestamp(),
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    deleted_at: firebase.firestore.FieldValue.serverTimestamp()
  });
}

export async function reorderDua(id, newOrderIndex) {
  return db.collection('duas').doc(id).update({ orderIndex: newOrderIndex });
}

export async function getAllDuas() {
  const snapshot = await db.collection('duas').orderBy('orderIndex').get();
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

export async function getTotalDuas() {
  const snapshot = await db.collection('duas').get();
  return snapshot.size;
}

export async function getDuaCategories() {
  const snapshot = await db.collection('dua_categories').orderBy('orderIndex').get();
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

export async function addDuaCategory(id, name) {
  const maxOrder = await db.collection('dua_categories').orderBy('orderIndex', 'desc').limit(1).get();
  const orderIndex = maxOrder.empty ? 0 : (maxOrder.docs[0].data().orderIndex || 0) + 1;
  return db.collection('dua_categories').doc(id).set({
    name,
    orderIndex,
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    created_at: firebase.firestore.FieldValue.serverTimestamp(),
    updated_at: "",
    deleted_at: "",
  });
}

export async function updateDuaCategory(id, updates) {
  return db.collection('dua_categories').doc(id).update({
    ...updates,
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    updated_at: firebase.firestore.FieldValue.serverTimestamp(),
  });
}

export async function updateDuaCategoryOrder(id, orderIndex) {
  return db.collection('dua_categories').doc(id).update({ orderIndex });
}

export async function deleteDuaCategory(id) {
  // Check if used
  const snapshot = await db.collection('duas').where('category', '==', id).where('isDeleted', '==', false).limit(1).get();
  if (!snapshot.empty) {
    throw new Error('Cannot delete: This category is currently in use by a Dua.');
  }
  return db.collection('dua_categories').doc(id).update({
    isDeleted: true,
    deletedAt: firebase.firestore.FieldValue.serverTimestamp(),
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    deleted_at: firebase.firestore.FieldValue.serverTimestamp()
  });
}